import { useEffect, useState } from 'react';

/**
 * Site Performance Leaderboard — sites ranked side by side.
 *
 * Read-only. Each site (a camera location) shows its event volume, severity mix,
 * still-open findings, and the trend versus the previous window, so it is obvious
 * at a glance which locations are excelling and which need attention. Fewer
 * events and a downward trend is better; the table is sorted attention-first
 * (most critical, then most open) by the server.
 */

type Win = '7d' | '30d' | '90d';

interface SiteRow {
  site: string;
  events: number;
  critical: number;
  warning: number;
  info: number;
  open: number;
  cameras: number;
  prevEvents: number;
  trendPct: number | null;
}
interface Leaderboard {
  window: Win;
  sites: SiteRow[];
  totals: { sites: number; events: number; critical: number; open: number };
}

function Trend({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="muted" style={{ fontSize: 12 }}>new</span>;
  if (pct === 0) return <span className="muted" style={{ fontSize: 12 }}>±0%</span>;
  // More events than last period is worse (red, up); fewer is better (green, down).
  const worse = pct > 0;
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: worse ? 'var(--bad)' : 'var(--good)' }}>
      {worse ? '↑' : '↓'} {Math.abs(pct)}%
    </span>
  );
}

export function SafetySiteLeaderboard({ apiBase }: { apiBase: string }) {
  const [win, setWin] = useState<Win>('30d');
  const [lb, setLb] = useState<Leaderboard | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${apiBase}/leaderboard?window=${win}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setLb)
      .catch(() => setLb(null))
      .finally(() => setLoading(false));
  }, [apiBase, win]);

  return (
    <div className="panel" style={{ marginTop: 20 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <strong>Site leaderboard</strong>
        <div className="row" style={{ gap: 0 }}>
          {(['7d', '30d', '90d'] as Win[]).map((w, i) => (
            <button
              key={w}
              onClick={() => setWin(w)}
              className="btn sm"
              style={{ borderRadius: i === 0 ? '6px 0 0 6px' : i === 2 ? '0 6px 6px 0' : 0, background: win === w ? 'var(--accent)' : undefined, color: win === w ? '#fff' : undefined }}
            >
              {w}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="muted" style={{ marginTop: 10 }}>Loading…</div>
      ) : !lb || lb.sites.length === 0 ? (
        <div className="muted" style={{ marginTop: 10 }}>
          No sites with events yet. A site is a camera's location — set one on each camera to compare locations here.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table style={{ width: '100%', fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr>
                <th style={{ width: 28 }}>#</th>
                <th>Site</th>
                <th style={{ textAlign: 'right' }}>Events</th>
                <th style={{ textAlign: 'right' }}>Critical</th>
                <th style={{ textAlign: 'right' }}>Open</th>
                <th>Severity mix</th>
                <th style={{ textAlign: 'right' }}>Trend</th>
              </tr>
            </thead>
            <tbody>
              {lb.sites.map((s, i) => {
                const mix = Math.max(1, s.critical + s.warning + s.info);
                return (
                  <tr key={s.site}>
                    <td className="muted">{i + 1}</td>
                    <td>
                      <span style={{ fontWeight: 600 }}>{s.site}</span>
                      <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>{s.cameras} cam{s.cameras === 1 ? '' : 's'}</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>{s.events}</td>
                    <td style={{ textAlign: 'right', color: s.critical > 0 ? 'var(--bad)' : undefined, fontWeight: s.critical > 0 ? 700 : 400 }}>{s.critical}</td>
                    <td style={{ textAlign: 'right', color: s.open > 0 ? 'var(--warn)' : undefined }}>{s.open}</td>
                    <td style={{ minWidth: 120 }}>
                      <span style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: 'var(--border)' }}>
                        <span style={{ width: `${(s.critical / mix) * 100}%`, background: 'var(--bad)' }} />
                        <span style={{ width: `${(s.warning / mix) * 100}%`, background: 'var(--warn)' }} />
                        <span style={{ width: `${(s.info / mix) * 100}%`, background: 'var(--teal)' }} />
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}><Trend pct={s.trendPct} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
